import { Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import { ProjectRowData } from "./types";
import { ProjectColumn } from "./columns/ProjectColumn";
import { LanguageColumn } from "./columns/LanguageColumn";
import { StatusColumn } from "./columns/StatusColumn";
import { ActionsColumn } from "./columns/ActionsColumn";

const columnHelper = createColumnHelper<ProjectRowData>();

interface CreateColumnsParams {
  navigate: (path: string) => void;
  isStatusChanged: (orderId: number, statusId: string) => string | null;
  tabIndex?: number;
}

const getTabStatus = (tabIdx: number) => {
  if (tabIdx === 0) return "Active";
  if (tabIdx === 1) return "Completed";
  return "Draft";
};

export const createProjectColumns = ({
  navigate,
  isStatusChanged,
  tabIndex = 0,
}: CreateColumnsParams) => [
  // Project # Column
  columnHelper.accessor("id", {
    id: "id",
    header: () => (
      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
        Project #
      </Text>
    ),
    cell: (info) => (
      <ProjectColumn
        projectId={info.getValue()}
        orderName={info.row.original.order_name}
      />
    ),
  }),

  // Language Column
  columnHelper.accessor("translated_from", {
    id: "languages",
    header: () => (
      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
        Language
      </Text>
    ),
    cell: (info) => (
      <LanguageColumn
        from={info.getValue() || ""}
        to={info.row.original.translated_to || ""}
      />
    ),
  }),

  // Type Column
  columnHelper.accessor("translation_type", {
    id: "translation_type",
    header: () => (
      <Text
        fontSize="14px"
        fontWeight="600"
        color="#0B0E10"
        textAlign="center"
        w="100%"
      >
        Type
      </Text>
    ),
    cell: (info) => {
      const rawValue = (info.getValue() || "Certified").toString();
      const value = rawValue.trim();
      const displayValue = value
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : "Certified";

      return (
        <Text
          color="#0B0E10"
          fontSize="16px"
          lineHeight="24px"
          textAlign="center"
          fontWeight="400"
        >
          {displayValue}
        </Text>
      );
    },
  }),

  // Status Column
  columnHelper.accessor("order_status_name", {
    id: "order_status_name",
    header: () => (
      <Text
        fontSize="14px"
        fontWeight="600"
        color="#0B0E10"
        textAlign="center"
        w="100%"
      >
        Status
      </Text>
    ),
    cell: (info) => {
      const hasChange =
        isStatusChanged(
          Number(info.row.original.order_id),
          info.row.original.order_status_id,
        ) !== null && Number(info.row.original.order_status_id ?? 10) !== 10;

      const displayStatus = getTabStatus(tabIndex);
      return (
        <StatusColumn status={displayStatus} hasStatusChange={hasChange} />
      );
    },
  }),

  // Pages Column
  columnHelper.accessor("page_count", {
    id: "page_count",
    header: () => (
      <Text
        fontSize="14px"
        fontWeight="600"
        color="#0B0E10"
        textAlign="center"
        w="100%"
      >
        Pages
      </Text>
    ),
    cell: (info) => (
      <Text
        color="#0B0E10"
        fontSize="16px"
        lineHeight="24px"
        fontWeight="400"
        textAlign="center"
      >
        {info.getValue() || ""}
      </Text>
    ),
  }),

  // Due Date Column
  columnHelper.accessor("order_due_at", {
    id: "order_due_at",
    header: () => (
      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
        Due Date
      </Text>
    ),
    cell: (info) => (
      <Text color="#0B0E10" fontSize="16px" lineHeight="24px" fontWeight="600">
        {info.getValue()
          ? moment(new Date(info.getValue())).format("YYYY-MM-DD")
          : "-"}
      </Text>
    ),
  }),

  // Price Column
  columnHelper.accessor("order_total", {
    id: "order_total",
    header: () => (
      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
        Price
      </Text>
    ),
    cell: (info) => (
      <Text color="#111827" fontSize="16px" lineHeight="24px" fontWeight="600">
        ${info.getValue() ? info.getValue() : "100"}
      </Text>
    ),
  }),

  // Actions Column
  columnHelper.accessor("actionCol", {
    id: "actionCol",
    header: () => (
      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
        Actions
      </Text>
    ),
    cell: (info) => {
      const isDraft = tabIndex === 2;
      const handleViewDetails = () => {
        if (isDraft) {
          localStorage.removeItem("uploadProject");
          localStorage.removeItem("orderStep");
          navigate(`/customer/main/${info.row.original.order_id}`);
        } else {
          navigate(`/customer/project/${info.row.original.order_id}`);
        }
      };

      return <ActionsColumn onViewDetails={handleViewDetails} />;
    },
  }),
];
